/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import { type FunctionComponent } from 'react'

import {
  EuiFlexGroup,
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiFlyoutHeader,
  EuiHorizontalRule,
  EuiSpacer,
  EuiTitle,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { CloudResource } from '@modules/billing-api/v1/types'

import Status from '../../../components/Status'

import Balance from './Balance'
import Scope from './Scope'
import TargetAmount from './TargetAmount'
import Thresholds from './Thresholds'
import Recipients from './Recipients'
import ActionButtons from './ActionButtons'

import type { Budget, Costs } from '../../types'

type Props = {
  budget: Budget
  cloudResources: CloudResource[] | undefined
  costs: Costs | undefined
  onClose: () => void
}

const BudgetDetails: FunctionComponent<Props> = ({ budget, cloudResources, costs, onClose }) => {
  const { active, amount, alerts, name, scope_type: scopeType, scope_values: scopeValues } = budget

  const titleId = 'billing.notifications.budgets.budget-details.flyout-title'

  return (
    <EuiFlyout
      data-test-subj='billing.notifications.budgets.budget-details.flyout'
      aria-labelledby={titleId}
      ownFocus={true}
      onClose={onClose}
      size='s'
    >
      <EuiFlyoutHeader hasBorder={true}>
        <EuiTitle size='m'>
          <h2 id={titleId}>{name}</h2>
        </EuiTitle>
        <EuiSpacer size='xs' />
        <Status active={active} />
      </EuiFlyoutHeader>
      <EuiFlyoutBody>
        <EuiFlexGroup direction='column' gutterSize='l'>
          <Balance budget={budget} cloudResources={cloudResources} costs={costs} />
          <TargetAmount amount={amount} />
          <Scope scopeType={scopeType} scopeValues={scopeValues} />
        </EuiFlexGroup>

        <EuiHorizontalRule margin='xl' />

        <EuiFlexGroup direction='column' gutterSize='l'>
          <EuiTitle size='xs'>
            <h3>
              <FormattedMessage
                id='billing.notifications.budgets.budget-details.alerts.title'
                defaultMessage='Alerts'
              />
            </h3>
          </EuiTitle>
          <Thresholds alerts={alerts} />
          <Recipients />
        </EuiFlexGroup>
      </EuiFlyoutBody>
      <EuiFlyoutFooter>
        <ActionButtons budget={budget} onClose={onClose} />
      </EuiFlyoutFooter>
    </EuiFlyout>
  )
}

export default BudgetDetails
